import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '../../lib/supabase';
import { 
  format, 
  addDays, 
  subDays, 
  differenceInDays, 
  parseISO, 
  startOfWeek, 
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths
} from 'date-fns';
import AnchorRegistryService from '../../services/AnchorRegistryService';
import TimelineSpreadsheet from './TimelineSpreadsheet';
import { 
  Calendar, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Filter,
  Eye,
  EyeOff,
  Settings,
  Edit3,
  Save,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Database,
  Play,
  Copy,
  Target,
  X,
  Check,
  Move,
  ArrowRight,
  Zap,
  Users,
  Brain,
  GraduationCap,
  Clock3,
  Layers,
  MoreHorizontal
} from 'lucide-react';

/**
 * Interactive Timeline Builder
 * Visual drag-and-drop timeline management with dependency validation
 * and real-time timeline pressure simulation
 */
const TimelineBuilder = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineView, setTimelineView] = useState('max_rd'); // max_rd, min_rd, max_ed, min_ed
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dependencies, setDependencies] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [timelineStart, setTimelineStart] = useState(new Date());
  const [timelineEnd, setTimelineEnd] = useState(addDays(new Date(), 365));
  const [zoomLevel, setZoomLevel] = useState(1); // 0.5x to 10x
  const canvasRef = useRef(null);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [taskGroups, setTaskGroups] = useState([]);
  const [focusAnchor, setFocusAnchor] = useState('all'); // Focus on specific deadline type
  const [selectedStudentId, setSelectedStudentId] = useState(null); // Student for anchor resolution
  const [students, setStudents] = useState([]);
  const [selectedTaskAnchorDate, setSelectedTaskAnchorDate] = useState(null);
  const [positionedTemplates, setPositionedTemplates] = useState([]); // State for positioned templates (since we need async anchor resolution)
  const [expandedStacks, setExpandedStacks] = useState(new Set()); // Track which stacks are expanded
  const [selectedApplicationRound, setSelectedApplicationRound] = useState('regular_decision'); // Selected round for modifiers
  const [showRoundModifiers, setShowRoundModifiers] = useState(false); // Show round modifier editing panel
  const [roundOrganization, setRoundOrganization] = useState('none'); // none, group, priority
  const [calendarViewMode, setCalendarViewMode] = useState('month'); // 'month' or 'week'
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  const [displayMode, setDisplayMode] = useState('calendar'); // 'calendar' or 'list'

  // Timeline dimensions and layout
  const TIMELINE_CONFIG = {
    dayWidth: 3 * zoomLevel, // pixels per day
    categoryHeight: 80, // pixels per category lane
    taskHeight: 60, // height of task blocks
    padding: 20,
    minTaskWidth: 80,
    maxTaskWidth: 200
  };

  // Application round configuration
  const APPLICATION_ROUNDS = {
    'early_decision': {
      name: 'Early Decision',
      shortName: 'ED',
      color: '#dc2626',
      bgColor: '#fef2f2',
      icon: Target,
      priority: 4,
      defaultDeadline: 'Nov 1'
    },
    'early_decision_2': {
      name: 'Early Decision II',
      shortName: 'ED2',
      color: '#ea580c',
      bgColor: '#fff7ed',
      icon: Target,
      priority: 3,
      defaultDeadline: 'Jan 1'
    },
    'early_action': {
      name: 'Early Action',
      shortName: 'EA',
      color: '#0891b2',
      bgColor: '#f0f9ff',
      icon: Clock3,
      priority: 2,
      defaultDeadline: 'Nov 1'
    },
    'regular_decision': {
      name: 'Regular Decision',
      shortName: 'RD',
      color: '#059669',
      bgColor: '#f0fdf4',
      icon: GraduationCap,
      priority: 1,
      defaultDeadline: 'Jan 1'
    }
  };

  // Task category configuration
  const TASK_CATEGORIES = {
    'application': {
      name: 'Application',
      color: 'bg-azure-500',
      textColor: 'text-azure-700',
      bgColor: 'bg-azure-50'
    },
    'essay': {
      name: 'Essay',
      color: 'bg-violet-500',
      textColor: 'text-violet-700',
      bgColor: 'bg-violet-50'
    },
    'testing': {
      name: 'Testing',
      color: 'bg-orange-500',
      textColor: 'text-orange-700',
      bgColor: 'bg-orange-50'
    },
    'financial_aid': {
      name: 'Financial Aid',
      color: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      bgColor: 'bg-emerald-50'
    },
    'profile': {
      name: 'Profile',
      color: 'bg-indigo-500',
      textColor: 'text-indigo-700',
      bgColor: 'bg-indigo-50'
    },
    'research': {
      name: 'Research',
      color: 'bg-yellow-500',
      textColor: 'text-yellow-700',
      bgColor: 'bg-yellow-50'
    }
  };

  // Load milestone templates and dependencies
  useEffect(() => {
    loadTimelineData();
    loadStudents();
  }, []);

  // Recalculate timeline when pressure changes or student selection changes
  useEffect(() => {
    if (templates.length > 0) {
      calculatePositionedTemplates();
      generateTaskGroups();
    }
  }, [templates, timelineView, zoomLevel, selectedStudentId, selectedCategories, expandedStacks, roundOrganization]);

  // Validate dependencies when positioned templates change
  useEffect(() => {
    if (positionedTemplates.length > 0) {
      validateDependencies();
    }
  }, [positionedTemplates]);

  // Sync selectedTask with positionedTemplates to keep date fields updated
  useEffect(() => {
    if (selectedTask && positionedTemplates.length > 0) {
      const updatedTask = positionedTemplates.find(t => t.id === selectedTask.id);
      if (updatedTask && updatedTask.scheduledDate !== selectedTask.scheduledDate) {
        console.log(`[TimelineBuilder] Syncing selectedTask date: ${format(updatedTask.scheduledDate, 'yyyy-MM-dd')}`);
        setSelectedTask(updatedTask);
      }
    }
  }, [positionedTemplates]);

  // Load anchor date when task is selected
  useEffect(() => {
    const loadSelectedTaskAnchorDate = async () => {
      if (selectedTask?.anchor_id) {
        try {
          const anchorDate = await getAnchorDate(selectedTask.anchor_id);
          setSelectedTaskAnchorDate(anchorDate);
        } catch (error) {
          console.error('Error loading selected task anchor date:', error);
          setSelectedTaskAnchorDate(null);
        }
      } else {
        setSelectedTaskAnchorDate(null);
      }
    };

    loadSelectedTaskAnchorDate();
  }, [selectedTask, selectedStudentId]);

  const loadTimelineData = async () => {
    try {
      console.log('[TimelineBuilder] Loading timeline data...');
      
      // Load templates
      const { data: templateData, error: templateError } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('offset_days', { ascending: true });

      if (templateError) throw templateError;

      // Load or create dependency relationships
      const dependencyData = createDependencyRelationships(templateData || []);
      
      setTemplates(templateData || []);
      setDependencies(dependencyData);
      console.log(`[TimelineBuilder] Loaded ${templateData?.length || 0} templates and ${dependencyData.length} dependencies`);
      
    } catch (error) {
      console.error('Error loading timeline data:', error);
      alert(`Error loading timeline data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      console.log('[TimelineBuilder] Loading students like ParentTestingHub pattern...');
      
      // First, load family members to get the students
      const { data: familyMembers, error: familyError } = await supabase
        .from('family_members')
        .select('id, name, auth_user_id')
        .not('auth_user_id', 'is', null);

      if (familyError) {
        console.error('[TimelineBuilder] Error loading family members:', familyError);
        throw familyError;
      }

      console.log(`[TimelineBuilder] Found ${familyMembers?.length || 0} family members`);
      console.log('[TimelineBuilder] Sample family member:', familyMembers?.[0]);

      if (!familyMembers || familyMembers.length === 0) {
        console.log('[TimelineBuilder] No family members found');
        setStudents([]);
        return;
      }

      // Now load their academic profiles using auth_user_id
      const authUserIds = familyMembers.map(member => member.auth_user_id);
      
      const { data: academicProfiles, error: profileError } = await supabase
        .from('student_academic_profiles')
        .select(`
          user_id,
          graduation_year,
          timeline_baseline_date,
          timeline_pressure,
          created_at
        `)
        .in('user_id', authUserIds);

      if (profileError) {
        console.error('[TimelineBuilder] Error loading academic profiles:', profileError);
        throw profileError;
      }

      console.log(`[TimelineBuilder] Found ${academicProfiles?.length || 0} academic profiles`);
      console.log('[TimelineBuilder] Sample academic profile:', academicProfiles?.[0]);

      // Combine family member data with academic profiles
      const studentData = familyMembers.map(member => {
        const profile = academicProfiles?.find(p => p.user_id === member.auth_user_id);
        
        // Parse name into first/last name
        const fullName = member.name || 'Unknown Student';
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        return {
          id: member.auth_user_id,
          family_member_id: member.id,
          first_name: firstName,
          last_name: lastName,
          graduation_year: profile?.graduation_year,
          timeline_baseline_date: profile?.timeline_baseline_date,
          timeline_pressure: profile?.timeline_pressure,
          created_at: profile?.created_at
        };
      }).filter(student => student.graduation_year) // Only include students with graduation years
      .sort((a, b) => a.first_name.localeCompare(b.first_name));

      setStudents(studentData);
      
      // Auto-select first student if available
      if (studentData.length > 0) {
        setSelectedStudentId(studentData[0].id);
        console.log(`[TimelineBuilder] Auto-selected student: ${studentData[0].first_name} ${studentData[0].last_name} (ID: ${studentData[0].id})`);
        console.log(`[TimelineBuilder] Student data:`, studentData[0]);
      }
      
      console.log(`[TimelineBuilder] Successfully loaded ${studentData.length} students for Timeline Builder`);
      
    } catch (error) {
      console.error('[TimelineBuilder] Error loading students:', error);
      setStudents([]);
    }
  };

  // Create dependency relationships based on template analysis
  const createDependencyRelationships = (templates) => {
    const dependencies = [];
    
    // Essay workflow dependencies
    const essayTemplates = templates.filter(t => t.category === 'essay');
    const brainstormTasks = essayTemplates.filter(t => t.title.toLowerCase().includes('brainstorm'));
    const draftTasks = essayTemplates.filter(t => t.title.toLowerCase().includes('draft'));
    const finalTasks = essayTemplates.filter(t => t.title.toLowerCase().includes('final'));

    // Get minimum gap days based on timeline pressure
    const getMinGapDays = (type) => {
      const pressureModifiers = {
        'crisis': 3,
        'intensive': 5,
        'steady': 7,
        'comprehensive': 14
      };
      
      const baseGaps = {
        'essay_workflow': 3,
        'application_workflow': 7
      };
      
      return Math.max(1, baseGaps[type] || pressureModifiers['steady']);
    };

    // Create essay workflow: brainstorm → draft → final
    brainstormTasks.forEach(brainstorm => {
      draftTasks.forEach(draft => {
        if (draft.anchor_id === brainstorm.anchor_id) {
          dependencies.push({
            id: `dep_${brainstorm.id}_${draft.id}`,
            fromTaskId: brainstorm.id,
            toTaskId: draft.id,
            minGapDays: getMinGapDays('essay_workflow'),
            type: 'essay_workflow',
            description: 'Brainstorm to draft'
          });
        }
      });
    });

    // Create draft → final dependencies
    draftTasks.forEach(draft => {
      finalTasks.forEach(final => {
        if (final.anchor_id === draft.anchor_id) {
          dependencies.push({
            id: `dep_${draft.id}_${final.id}`,
            fromTaskId: draft.id,
            toTaskId: final.id,
            minGapDays: getMinGapDays('essay_workflow'),
            type: 'essay_workflow',
            description: 'Draft to final'
          });
        }
      });
    });

    // Application workflow dependencies
    const appTemplates = templates.filter(t => t.category === 'application');
    const researchTasks = appTemplates.filter(t => t.title.toLowerCase().includes('research'));
    const submitTasks = appTemplates.filter(t => t.title.toLowerCase().includes('submit'));

    researchTasks.forEach(research => {
      submitTasks.forEach(submit => {
        if (submit.anchor_id === research.anchor_id) {
          dependencies.push({
            id: `dep_${research.id}_${submit.id}`,
            fromTaskId: research.id,
            toTaskId: submit.id,
            minGapDays: getMinGapDays('application_workflow'),
            type: 'application_workflow',
            description: 'Research to submit'
          });
        }
      });
    });

    return dependencies;
  };

  // Calculate timeline positions based on current pressure and zoom
  const calculateTimelinePositions = () => {
    // Don't update state here - this was causing the infinite loop
    console.log(`[TimelineBuilder] Calculating positions for ${templates.length} templates`);
  };

  // Get anchor date using AnchorRegistryService for consistency with milestone generation
  const getAnchorDate = async (anchorId) => {
    try {
      const selectedStudent = students.find(s => s.id === selectedStudentId);
      
      if (selectedStudent) {
        // For college-specific anchors, use fallback dates to avoid looping
        const collegeSpecificAnchors = ['rd_deadline', 'ed_deadline', 'ea_deadline', 'finaid_deadline', 'css_deadline'];
        
        if (collegeSpecificAnchors.includes(anchorId)) {
          // Use standard academic year dates
          const seniorYear = selectedStudent.graduation_year || new Date().getFullYear();
          const fallbackDates = {
            'ed_deadline': new Date(seniorYear - 1, 10, 1), // Nov 1
            'ea_deadline': new Date(seniorYear - 1, 10, 1), // Nov 1  
            'rd_deadline': new Date(seniorYear, 0, 1),      // Jan 1
            'finaid_deadline': new Date(seniorYear, 1, 1),  // Feb 1
            'css_deadline': new Date(seniorYear, 1, 1)      // Feb 1
          };
          const anchorDate = fallbackDates[anchorId];
          console.log(`[getAnchorDate] Using fallback for ${anchorId}:`, format(anchorDate, 'yyyy-MM-dd'));
          return anchorDate;
        }
        
        // Use AnchorRegistryService for student-specific anchors
        const anchorDate = await AnchorRegistryService.calculateAnchorDate(
          anchorId, 
          selectedStudent,
          null // college - not needed for student-specific anchors
        );
        console.log(`[TimelineBuilder] Resolved anchor ${anchorId} for student ${selectedStudent.first_name}:`, format(anchorDate, 'yyyy-MM-dd'));
        return anchorDate;
      } else {
        // Fallback to mock dates for preview mode when no student selected
        const currentYear = new Date().getFullYear();
        const academicYear = new Date().getMonth() >= 8 ? currentYear : currentYear - 1;
        
        const fallbackDates = {
          'timeline_baseline': new Date(), // Current date
          'rolling_admission': new Date(),
          'ed_deadline': new Date(academicYear, 10, 1), // Nov 1
          'ea_deadline': new Date(academicYear, 10, 15), // Nov 15  
          'rd_deadline': new Date(academicYear + 1, 0, 1), // Jan 1 (next calendar year)
          'national_decision_day': new Date(academicYear + 1, 4, 1), // May 1
        };
        
        const fallbackDate = fallbackDates[anchorId] || fallbackDates['rd_deadline'];
        console.log(`[TimelineBuilder] Using fallback anchor ${anchorId}:`, format(fallbackDate, 'yyyy-MM-dd'));
        return fallbackDate;
      }
    } catch (error) {
      console.error(`[TimelineBuilder] Error resolving anchor ${anchorId}:`, error);
      // Return fallback date on error
      return new Date();
    }
  };

  // Get Y position for category lane
  const getCategoryLaneY = (category) => {
    const categories = ['research', 'testing', 'essay', 'application', 'financial_aid', 'profile', 'strategy'];
    const index = categories.indexOf(category);
    return (index >= 0 ? index : 0) * TIMELINE_CONFIG.categoryHeight + TIMELINE_CONFIG.padding + 30; // Add 30px for month headers
  };

  // Validate dependencies and find conflicts
  const validateDependencies = () => {
    if (dependencies.length === 0 || positionedTemplates.length === 0) return;
    
    const conflicts = [];
    
    dependencies.forEach(dep => {
      const fromTask = positionedTemplates.find(t => t.id === dep.fromTaskId);
      const toTask = positionedTemplates.find(t => t.id === dep.toTaskId);
      
      if (fromTask && toTask) {
        const actualGap = differenceInDays(toTask.scheduledDate, fromTask.scheduledDate);
        
        if (actualGap < dep.minGapDays) {
          conflicts.push({
            id: `conflict_${dep.id}`,
            type: 'insufficient_gap',
            severity: actualGap < 1 ? 'critical' : 'warning',
            dependency: dep,
            fromTask,
            toTask,
            actualGap,
            requiredGap: dep.minGapDays,
            message: `"${fromTask.title}" to "${toTask.title}" has ${actualGap} days (needs ${dep.minGapDays}+)`
          });
        }
      }
    });

    setConflicts(conflicts);
    console.log(`[TimelineBuilder] Found ${conflicts.length} dependency conflicts`);
  };

  // Handle drag start
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    console.log(`[TimelineBuilder] Started dragging task: ${task.title}`);
  };

  // Handle drop
  const handleDrop = async (e) => {
    e.preventDefault();
    if (!draggedTask) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate new date based on drop position
    const daysDelta = Math.round(x / TIMELINE_CONFIG.dayWidth);
    const newDate = addDays(timelineStart, daysDelta);
    
    // Calculate new offset from anchor (async)
    const anchorDate = await getAnchorDate(draggedTask.anchor_id);
    const newOffsetFromAnchor = differenceInDays(anchorDate, newDate);
    const newOffsetDays = Math.abs(newOffsetFromAnchor);
    const newOffsetDirection = newOffsetFromAnchor >= 0 ? 'before' : 'after';

    console.log(`[TimelineBuilder] Dropping task at ${format(newDate, 'MMM dd')} (offset: ${newOffsetDays} days ${newOffsetDirection})`);
    console.log(`[TimelineBuilder] Using anchor date: ${format(anchorDate, 'MMM dd, yyyy')}`);
    
    // If we're in a different pressure mode than native, convert offset back to native pressure
    const nativePressure = draggedTask.native_pressure || 'comprehensive';
    const currentPressure = getCurrentPressure();
    const newNativeOffset = nativePressure === currentPressure 
      ? newOffsetDays 
      : convertToNativePressure(newOffsetDays, currentPressure, nativePressure);
    
    console.log(`[TimelineBuilder] Converting ${newOffsetDays}d (${currentPressure}) → ${newNativeOffset}d (${nativePressure})`);
    
    // Update template with native pressure values (using signed offset)
    await updateTemplate(draggedTask.id, {
      offset_days: newNativeOffset,
      native_pressure: nativePressure // Ensure native_pressure is preserved
    });

    setDraggedTask(null);
  };

  // Validate template updates for realistic timelines
  const validateTemplateUpdate = (template, updates) => {
    const warnings = [];
    
    // Check if offset_days is being updated
    if (updates.offset_days !== undefined) {
      const constraints = getTaskConstraints(template);
      const newOffset = updates.offset_days;
      
      if (newOffset > constraints.maxOffsetDays) {
        warnings.push(`Warning: ${newOffset} days may be too far in advance for "${template.title}". Consider limiting to ${constraints.maxOffsetDays} days. (${constraints.reason})`);
      }
      
      if (newOffset < constraints.minOffsetDays) {
        warnings.push(`Warning: ${newOffset} days may be too close for "${template.title}". Consider at least ${constraints.minOffsetDays} days. (${constraints.reason})`);
      }
      
      // Specific warnings for decision tracking tasks
      if (template.title?.toLowerCase().includes('decision') && newOffset > 180) {
        warnings.push(`Warning: Decision tracking tasks are typically scheduled within 6 months of the deadline, not ${newOffset} days in advance.`);
      }
    }
    
    return warnings;
  };

  // Update template in database and local state
  const updateTemplate = async (templateId, updates) => {
    try {
      console.log(`[TimelineBuilder] Updating template ${templateId}:`, updates);

      // Always sync offset_days with max_rd_offset_days for backwards compatibility
      if ('max_rd_offset_days' in updates) {
        updates.offset_days = updates.max_rd_offset_days;
        console.log(`[TimelineBuilder] Syncing offset_days with max_rd_offset_days: ${updates.offset_days}`);
      }

      // Find the template for validation
      const template = templates.find(t => t.id === templateId);
      if (template) {
        const warnings = validateTemplateUpdate(template, updates);
        if (warnings.length > 0) {
          console.warn(`[TimelineBuilder] Validation warnings for ${template.title}:`, warnings);
          // Show warnings to user (non-blocking)
          warnings.forEach(warning => {
            console.log(`[TimelineBuilder] ${warning}`);
          });
        }
      }

      const { error } = await supabase
        .from('milestone_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId);

      if (error) throw error;

      // Update local state and recalculate positions
      setTemplates(prev => prev.map(t => 
        t.id === templateId ? { ...t, ...updates } : t
      ));

      // Update selected task if it's the one being modified
      setSelectedTask(prev => 
        prev && prev.id === templateId 
          ? { ...prev, ...updates }
          : prev
      );

      // Templates updated - positioned templates will recalculate automatically via useEffect
      console.log(`[TimelineBuilder] Template ${templateId} updated successfully - triggering automatic recalculation`)

      console.log(`[TimelineBuilder] Template ${templateId} updated successfully`);
      return true;
    } catch (error) {
      console.error('Error updating template:', error);
      alert(`Error updating template: ${error.message}`);
      return false;
    }
  };

  // Application round modifier helpers
  const hasApplicationRoundModifiers = (template) => {
    return template.application_round_modifier && 
           Object.keys(template.application_round_modifier).length > 0;
  };

  const getTemplateRoundModifier = (template, round) => {
    if (!template.application_round_modifier) return null;
    return template.application_round_modifier[round] || null;
  };

  const updateApplicationRoundModifier = async (templateId, round, modifier) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    const currentModifiers = template.application_round_modifier || {};
    const newModifiers = {
      ...currentModifiers,
      [round]: modifier
    };

    await updateTemplate(templateId, { 
      application_round_modifier: newModifiers 
    });
  };

  const removeApplicationRoundModifier = async (templateId, round) => {
    const template = templates.find(t => t.id === templateId);
    if (!template || !template.application_round_modifier) return;

    const newModifiers = { ...template.application_round_modifier };
    delete newModifiers[round];

    await updateTemplate(templateId, { 
      application_round_modifier: Object.keys(newModifiers).length > 0 ? newModifiers : null 
    });
  };

  const getTaskRoundIndicators = (template) => {
    if (!hasApplicationRoundModifiers(template)) return [];
    
    return Object.keys(template.application_round_modifier).map(round => ({
      round,
      config: APPLICATION_ROUNDS[round],
      modifier: template.application_round_modifier[round]
    }));
  };

  const getSmartConstraintsForRound = (template, round) => {
    const constraints = getTaskConstraints(template);
    const roundConfig = APPLICATION_ROUNDS[round];
    
    // ED tasks need more lead time than RD tasks
    if (round === 'early_decision' || round === 'early_decision_2') {
      return {
        ...constraints,
        minOffsetDays: Math.max(constraints.minOffsetDays, 14), // At least 2 weeks for ED
        recommendedOffsetDays: Math.max(constraints.recommendedOffsetDays || 30, 30),
        reason: `${roundConfig.name} applications need extra preparation time`
      };
    }
    
    return constraints;
  };

  // Apply round organization to templates
  const applyRoundOrganization = (templates, organizationType) => {
    if (organizationType === 'none') return templates;
    
    const templatesWithRounds = templates.map(template => {
      // Determine primary round based on application_round_modifier keys
      const roundModifiers = template.application_round_modifier || {};
      const rounds = Object.keys(roundModifiers);
      
      // If no round modifiers, classify based on task type and anchor
      let primaryRound = 'regular_decision'; // default
      if (rounds.length > 0) {
        // Use the highest priority round
        const priorityOrder = ['early_decision', 'early_decision_2', 'early_action', 'regular_decision'];
        primaryRound = priorityOrder.find(round => rounds.includes(round)) || rounds[0];
      } else {
        // Infer from anchor_id
        if (template.anchor_id === 'ed_deadline') {
          primaryRound = 'early_decision';
        } else if (template.anchor_id === 'ea_deadline') {
          primaryRound = 'early_action';
        } else if (template.anchor_id === 'rd_deadline') {
          primaryRound = 'regular_decision';
        }
      }
      
      return {
        ...template,
        primaryRound,
        roundConfig: APPLICATION_ROUNDS[primaryRound],
        hasRoundModifiers: rounds.length > 0
      };
    });
    
    if (organizationType === 'group') {
      // Group by round and adjust Y positions to create visual groupings
      const roundGroups = Object.keys(APPLICATION_ROUNDS).reduce((groups, round) => {
        groups[round] = templatesWithRounds.filter(t => t.primaryRound === round);
        return groups;
      }, {});
      
      let organizedTemplates = [];
      let currentYOffset = 0;
      
      // Process each round in priority order
      Object.entries(APPLICATION_ROUNDS)
        .sort(([,a], [,b]) => b.priority - a.priority) // Sort by priority descending
        .forEach(([roundKey, roundConfig]) => {
          const roundTemplates = roundGroups[roundKey];
          if (roundTemplates.length === 0) return;
          
          // Add round header spacing
          const headerHeight = 40;
          currentYOffset += headerHeight;
          
          // Adjust Y positions for this group
          const adjustedTemplates = roundTemplates.map(template => ({
            ...template,
            yPosition: template.yPosition + currentYOffset,
            isRoundGrouped: true,
            roundGroupStart: currentYOffset - headerHeight,
            roundGroupTitle: `${roundConfig.name} (${roundTemplates.length} tasks)`
          }));
          
          organizedTemplates.push(...adjustedTemplates);
          
          // Calculate group height and add spacing
          const maxY = Math.max(...adjustedTemplates.map(t => t.yPosition + TIMELINE_CONFIG.taskHeight));
          currentYOffset = maxY + 20; // 20px spacing between groups
        });
      
      return organizedTemplates;
      
    } else if (organizationType === 'priority') {
      // Sort by round priority, then by scheduled date within each round
      return templatesWithRounds.sort((a, b) => {
        const priorityDiff = b.roundConfig.priority - a.roundConfig.priority;
        if (priorityDiff !== 0) return priorityDiff;
        
        // Within same priority, sort by scheduled date
        return new Date(a.scheduledDate) - new Date(b.scheduledDate);
      });
    }
    
    return templatesWithRounds;
  };

  // Get anchor label
  const getAnchorLabel = (anchor) => {
    const labels = {
      'rolling_admission': 'Rolling/Ongoing',
      'rd_deadline': 'Regular Decision',
      'ea_deadline': 'Early Action',
      'ed_deadline': 'Early Decision',
      'national_decision_day': 'Decision Day'
    };
    return labels[anchor] || anchor;
  };

  // Generate task grouping suggestions
  const generateTaskGroups = () => {
    const groups = [];
    
    // Essay workflow groups
    const essayGroups = groupEssayWorkflows();
    groups.push(...essayGroups);
    
    // Application workflow groups  
    const applicationGroups = groupApplicationWorkflows();
    groups.push(...applicationGroups);
    
    // Research batching opportunities
    const researchGroups = groupResearchTasks();
    groups.push(...researchGroups);
    
    setTaskGroups(groups);
    console.log(`[TimelineBuilder] Generated ${groups.length} task grouping suggestions`);
  };

  const groupEssayWorkflows = () => {
    const groups = [];
    const essayTasks = filteredTemplates.filter(t => t.category === 'essay');
    
    // Group by anchor (college application type)
    const anchorGroups = essayTasks.reduce((acc, task) => {
      if (!acc[task.anchor_id]) acc[task.anchor_id] = [];
      acc[task.anchor_id].push(task);
      return acc;
    }, {});

    Object.entries(anchorGroups).forEach(([anchor, tasks]) => {
      if (tasks.length >= 3) {
        const brainstorm = tasks.find(t => t.title.toLowerCase().includes('brainstorm'));
        const draft = tasks.find(t => t.title.toLowerCase().includes('draft'));
        const final = tasks.find(t => t.title.toLowerCase().includes('final'));
        
        if (brainstorm && draft && final) {
          groups.push({
            id: `essay_workflow_${anchor}`,
            type: 'essay_workflow',
            anchor,
            tasks: [brainstorm, draft, final],
            title: `Essay Workflow - ${getAnchorLabel(anchor)}`,
            description: 'Brainstorm → Draft → Final sequence',
            benefit: 'Maintains creative flow and ensures sufficient revision time',
            priority: 'high'
          });
        }
      }
    });

    return groups;
  };

  const groupApplicationWorkflows = () => {
    const groups = [];
    const appTasks = filteredTemplates.filter(t => t.category === 'application');
    
    // Find complementary application tasks
    const researchTasks = appTasks.filter(t => t.title.toLowerCase().includes('research'));
    const submitTasks = appTasks.filter(t => t.title.toLowerCase().includes('submit'));
    
    researchTasks.forEach(research => {
      const relatedSubmit = submitTasks.find(s => s.anchor_id === research.anchor_id);
      if (relatedSubmit) {
        groups.push({
          id: `app_workflow_${research.anchor_id}`,
          type: 'application_workflow',
          anchor: research.anchor_id,
          tasks: [research, relatedSubmit],
          title: `Application Process - ${getAnchorLabel(research.anchor_id)}`,
          description: 'Research requirements then submit application',
          benefit: 'Ensures thorough preparation before submission',
          priority: 'high'
        });
      }
    });

    return groups;
  };

  const groupResearchTasks = () => {
    const groups = [];
    const researchTasks = filteredTemplates.filter(t => 
      t.title.toLowerCase().includes('research') ||
      t.title.toLowerCase().includes('identify') ||
      t.category === 'research'
    );

    if (researchTasks.length >= 3) {
      // Find tasks that could be batched together
      const batchableTasks = researchTasks.filter(task => {
        const timeDiff = Math.abs(differenceInDays(task.scheduledDate, new Date()));
        return timeDiff < 30; // Within 30 days of each other
      });

      if (batchableTasks.length >= 2) {
        groups.push({
          id: 'research_batch',
          type: 'research_batch',
          tasks: batchableTasks.slice(0, 4), // Limit to 4 tasks per batch
          title: 'Research Session Batch',
          description: 'Group related research tasks for focused work sessions',
          benefit: 'Reduces context switching and increases research efficiency',
          priority: 'medium'
        });
      }
    }

    return groups;
  };

  // Auto-optimize timeline spacing
  const optimizeTimelineSpacing = () => {
    console.log('[TimelineBuilder] Optimizing timeline spacing...');
    
    // Find overcrowded periods using positioned templates
    const tasksPerDay = {};
    positionedTemplates.forEach(task => {
      const dateKey = format(task.scheduledDate, 'yyyy-MM-dd');
      if (!tasksPerDay[dateKey]) tasksPerDay[dateKey] = [];
      tasksPerDay[dateKey].push(task);
    });

    const overcrowdedDays = Object.entries(tasksPerDay)
      .filter(([date, tasks]) => tasks.length > 3)
      .map(([date, tasks]) => ({ date, tasks, count: tasks.length }));

    if (overcrowdedDays.length > 0) {
      console.log(`[TimelineBuilder] Found ${overcrowdedDays.length} overcrowded days to optimize`);
      alert(`Found ${overcrowdedDays.length} overcrowded days. Auto-optimization not yet implemented.`);
      return overcrowdedDays;
    } else {
      alert('Timeline spacing looks good! No overcrowded days found.');
    }
    
    return [];
  };

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(templates.map(t => t.category))].sort();
    return ['all', ...cats];
  }, [templates]);

  // Timeline view configurations  
  const VIEW_CONFIGS = {
    max_rd: {
      label: 'MAX (RD)',
      icon: Brain,
      color: 'bg-emerald-500',
      description: 'Comprehensive Regular Decision - 18 month timeline with all tasks',
      deadline: 'Regular Decision',
      scope: 'comprehensive',
      legacyPressure: 'comprehensive' // Temporary mapping for backward compatibility
    },
    min_rd: {
      label: 'MIN (RD)', 
      icon: Zap,
      color: 'bg-red-500',
      description: 'Crisis Regular Decision - Essential tasks only, compressed timeline',
      deadline: 'Regular Decision',
      scope: 'essential',
      legacyPressure: 'crisis' // Temporary mapping for backward compatibility
    },
    max_ed: {
      label: 'MAX (ED)',
      icon: Target,
      color: 'bg-azure-500', 
      description: 'Comprehensive Early Decision - 18 month timeline ending at ED deadline',
      deadline: 'Early Decision',
      scope: 'comprehensive',
      legacyPressure: 'comprehensive' // Temporary mapping for backward compatibility
    },
    min_ed: {
      label: 'MIN (ED)',
      icon: Users,
      color: 'bg-orange-500',
      description: 'Crisis Early Decision - Essential ED tasks, compressed timeline',
      deadline: 'Early Decision', 
      scope: 'essential',
      legacyPressure: 'crisis' // Temporary mapping for backward compatibility
    }
  };

  // Helper function to get current pressure equivalent for legacy compatibility
  const getCurrentPressure = () => VIEW_CONFIGS[timelineView]?.legacyPressure || 'comprehensive';

  // Helper function to get the correct offset field names for current view
  const getOffsetFields = (view = timelineView) => {
    const fieldMap = {
      'max_rd': 'max_rd_offset_days',
      'min_rd': 'min_rd_offset_days',
      'max_ed': 'max_ed_offset_days',
      'min_ed': 'min_ed_offset_days'
    };
    return fieldMap[view] || fieldMap['max_rd']; // Default to max_rd
  };

  // Helper function to get offset value from template based on current view
  const getOffsetValue = (template, view = timelineView) => {
    // For college-specific tasks: use min/max ED bounds when in ED views
    // For global tasks: always use RD bounds regardless of ED view
    let effectiveView = view;
    
    if (!template.is_college_specific) {
      // Global tasks (personal essay, FAFSA, etc.) always use RD bounds
      // Convert ED views to corresponding RD views
      if (view === 'max_ed') effectiveView = 'max_rd';
      if (view === 'min_ed') effectiveView = 'min_rd';
    }
    
    const fieldName = getOffsetFields(effectiveView);
    const offsetDays = template[fieldName] || template.offset_days || 7; // Fallback to legacy field
    return offsetDays;
  };

  // Filter templates based on timeline pressure, categories, and anchor focus
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Filter by timeline view - show/hide tasks based on min vs max views
    const isMinView = timelineView.includes('min_');
    if (isMinView) {
      // MIN views only show essential tasks
      filtered = filtered.filter(t => t.show_in_min_views !== false);
    }
    // MAX views show all tasks (no filtering needed)
    
    // Filter by ED views - only show college-specific tasks in ED modes
    const isEDView = timelineView.includes('_ed');
    if (isEDView) {
      // ED views only show college-specific tasks (tasks that get duplicated per college)
      filtered = filtered.filter(t => t.is_college_specific === true);
    }
    // RD views show all tasks (both college-specific and global)

    // Filter by categories
    if (!selectedCategories.includes('all')) {
      filtered = filtered.filter(t => selectedCategories.includes(t.category));
    }

    // Filter by anchor/deadline focus
    if (focusAnchor !== 'all') {
      if (focusAnchor === 'ed_ea') {
        filtered = filtered.filter(t => ['ed_deadline', 'ea_deadline'].includes(t.anchor_id));
      } else if (focusAnchor === 'rd') {
        filtered = filtered.filter(t => t.anchor_id === 'rd_deadline');
      } else {
        filtered = filtered.filter(t => t.anchor_id === focusAnchor);
      }
    }

    return filtered;
  }, [templates, timelineView, selectedCategories, focusAnchor]);

  // Calendar helper functions
  const calendarDays = useMemo(() => {
    if (calendarViewMode === 'week') {
      const weekStart = startOfWeek(currentCalendarDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentCalendarDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    } else {
      const monthStart = startOfMonth(currentCalendarDate);
      const monthEnd = endOfMonth(currentCalendarDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    }
  }, [currentCalendarDate, calendarViewMode]);

  // Convert positioned templates to calendar tasks by date
  const calendarTasks = useMemo(() => {
    const tasksByDate = {};
    
    positionedTemplates.forEach(task => {
      if (task.scheduledDate) {
        const dateKey = format(task.scheduledDate, 'yyyy-MM-dd');
        if (!tasksByDate[dateKey]) {
          tasksByDate[dateKey] = [];
        }
        tasksByDate[dateKey].push(task);
      }
    });
    
    return tasksByDate;
  }, [positionedTemplates]);

  // Get tasks for a specific calendar day
  const getTasksForDay = (date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return calendarTasks[dateKey] || [];
  };

  // Task-specific pressure constraints to prevent unrealistic timelines
  const getTaskConstraints = (template) => {
    // Define constraints based on task content and category
    const taskTitle = template.title?.toLowerCase() || '';
    const category = template.category || '';
    
    // Tasks that should have limited pressure variation
    const constraintRules = [
      // Decision tracking tasks - can't be too far in advance
      {
        keywords: ['track', 'decision', 'admission', 'result'],
        maxOffsetDays: 180, // Max 6 months
        reason: 'Decisions cannot be tracked too far in advance'
      },
      // Interview tasks - usually close to deadlines
      {
        keywords: ['interview', 'meeting', 'call'],
        maxOffsetDays: 90, // Max 3 months
        reason: 'Interviews are typically scheduled closer to deadlines'
      },
      // Application submission - has natural limits
      {
        keywords: ['submit', 'apply', 'send'],
        category: 'application',
        maxOffsetDays: 120, // Max 4 months before deadline
        minOffsetDays: 1, // At least 1 day before
        reason: 'Applications have practical submission windows'
      },
      // Testing tasks - have specific seasons/windows
      {
        keywords: ['test', 'sat', 'act', 'exam'],
        category: 'testing',
        maxOffsetDays: 365, // Max 1 year prep
        minOffsetDays: 30, // At least 1 month prep
        reason: 'Test preparation has natural timing windows'
      }
    ];
    
    // Find matching constraint rule
    for (const rule of constraintRules) {
      const matchesKeyword = rule.keywords?.some(keyword => 
        taskTitle.includes(keyword)
      );
      const matchesCategory = !rule.category || rule.category === category;
      
      if (matchesKeyword && matchesCategory) {
        return {
          maxOffsetDays: rule.maxOffsetDays || Infinity,
          minOffsetDays: rule.minOffsetDays || 1,
          reason: rule.reason
        };
      }
    }
    
    // Default constraints for uncategorized tasks
    return {
      maxOffsetDays: 500, // ~1.4 years max
      minOffsetDays: 1, // At least 1 day
      reason: 'General task limits'
    };
  };

  // Bidirectional pressure calculation system with constraints
  const calculatePressureOffset = (template, targetPressure) => {
    // Timeline Pressure Modifiers (base units for calculation)
    const pressureModifiers = {
      'crisis': 3,
      'intensive': 5, 
      'steady': 7,
      'comprehensive': 14
    };
    
    // Application Round Modifiers (absolute minimums)
    const roundModifiers = {
      'ed_deadline': 60,
      'ea_deadline': 60,
      'rd_deadline': 3
    };
    
    const nativePressure = template.native_pressure || 'comprehensive';
    const nativeOffset = template.offset_days;
    
    // Calculate offset for target pressure based on native values
    const nativePressureValue = pressureModifiers[nativePressure];
    const targetPressureValue = pressureModifiers[targetPressure];
    
    // Scale the offset based on pressure ratio
    const scaleFactor = targetPressureValue / nativePressureValue;
    let adjustedOffset = Math.max(1, Math.round(nativeOffset * scaleFactor));
    
    // Apply application round modifiers if applicable (absolute minimums)
    if (roundModifiers[template.anchor_id] && template.category === 'application') {
      adjustedOffset = Math.max(adjustedOffset, roundModifiers[template.anchor_id]);
    }
    
    // Apply task-specific constraints to prevent unrealistic timelines
    const constraints = getTaskConstraints(template);
    const constrainedOffset = Math.min(
      Math.max(adjustedOffset, constraints.minOffsetDays),
      constraints.maxOffsetDays
    );
    
    // Log constraint application if offset was changed
    if (constrainedOffset !== adjustedOffset) {
      console.log(`[TimelineBuilder] Applied constraint to "${template.title}": ${adjustedOffset}d → ${constrainedOffset}d (${constraints.reason})`);
    }
    
    return constrainedOffset;
  };

  // Convert offset from current viewing pressure back to native pressure
  const convertToNativePressure = (currentOffset, currentPressure, nativePressure) => {
    const pressureModifiers = {
      'crisis': 3,
      'intensive': 5, 
      'steady': 7,
      'comprehensive': 14
    };
    
    const currentPressureValue = pressureModifiers[currentPressure];
    const nativePressureValue = pressureModifiers[nativePressure];
    
    const scaleFactor = nativePressureValue / currentPressureValue;
    return Math.max(1, Math.round(currentOffset * scaleFactor));
  };

  // Calculate positioned templates with vertical stacking for overlapping tasks
  const calculatePositionedTemplates = useCallback(async () => {
    try {
      console.log(`[TimelineBuilder] Calculating positioned templates for ${filteredTemplates.length} templates`);
      
      // Calculate templates with async anchor resolution
      const templatesWithPositions = await Promise.all(
        filteredTemplates.map(async (template) => {
          console.log(`\n[TimelineBuilder] ===== PROCESSING TEMPLATE: ${template.title} =====`);
          console.log(`[TimelineBuilder] Template ID: ${template.id}`);
          console.log(`[TimelineBuilder] Category: ${template.category}`);
          console.log(`[TimelineBuilder] Anchor ID: ${template.anchor_id}`);
          // Get offset values for current view (new min/max system)
          const currentOffset = getOffsetValue(template, timelineView);
          console.log(`[TimelineBuilder] View: ${timelineView}, Offset: ${currentOffset.days}d ${currentOffset.direction}`);
          
          const adjustedOffsetDays = currentOffset.days;
          const offsetDirection = currentOffset.direction;
          
          // Use the same target date logic as TimelineSpreadsheet for consistency
          const getTargetDateForView = (template, viewType) => {
            let targetDateField;
            switch(viewType) {
              case 'max_rd': targetDateField = 'max_rd_target_date'; break;
              case 'min_rd': targetDateField = 'min_rd_target_date'; break;
              case 'max_ed': targetDateField = 'max_ed_target_date'; break;
              case 'min_ed': targetDateField = 'min_ed_target_date'; break;
              default: targetDateField = 'max_rd_target_date'; break;
            }
            
            const targetDate = template[targetDateField];
            if (!targetDate) return null;
            
            try {
              const dateObj = new Date(targetDate + 'T00:00:00');
              if (isNaN(dateObj.getTime())) return null;
              return dateObj;
            } catch (error) {
              console.error('Error parsing target date:', error);
              return null;
            }
          };

          // Get the scheduled date from the stored target date field
          let scheduledDate = getTargetDateForView(template, timelineView);
          
          // Fallback to anchor calculation only if no target date is stored
          if (!scheduledDate) {
            console.log(`[TimelineBuilder] No target date found, falling back to anchor calculation`);
            const anchorDate = await getAnchorDate(template.anchor_id);
            console.log(`[TimelineBuilder] Resolved anchor date: ${format(anchorDate, 'yyyy-MM-dd (EEEE)')} (${template.anchor_id})`);
            
            scheduledDate = offsetDirection === 'after' 
              ? addDays(anchorDate, adjustedOffsetDays)
              : subDays(anchorDate, adjustedOffsetDays);
          } else {
            console.log(`[TimelineBuilder] Using stored target date: ${format(scheduledDate, 'yyyy-MM-dd (EEEE)')} from view ${timelineView}`);
          }
            
          console.log(`[TimelineBuilder] Final scheduled date: ${format(scheduledDate, 'yyyy-MM-dd (EEEE)')} ${scheduledDate ? '(from stored target date)' : '(calculated from anchor)'}`);
          
          // Log comparison data for milestone generation service verification
          const selectedStudent = students.find(s => s.id === selectedStudentId);
          if (selectedStudent) {
            console.log(`[TimelineBuilder] 🔍 VERIFICATION DATA FOR MILESTONE GENERATION:`);
            console.log(`[TimelineBuilder] Student: ${selectedStudent.first_name} ${selectedStudent.last_name} (ID: ${selectedStudent.id})`);
            console.log(`[TimelineBuilder] Student Timeline Baseline: ${selectedStudent.timeline_baseline_date || selectedStudent.created_at || 'N/A'}`);
            console.log(`[TimelineBuilder] Student Timeline Pressure: ${selectedStudent.timeline_pressure || 'N/A'}`);
            console.log(`[TimelineBuilder] Student Graduation Year: ${selectedStudent.graduation_year || 'N/A'}`);
            console.log(`[TimelineBuilder] Expected TaskDateCalculationService Input:`);
            console.log(`[TimelineBuilder]   - template.anchor_id: "${template.anchor_id}"`);
            console.log(`[TimelineBuilder]   - template.offset_days: ${template.offset_days}`);
            console.log(`[TimelineBuilder]   - template.native_pressure: "${template.native_pressure || 'comprehensive'}"`);
            console.log(`[TimelineBuilder]   - student.timeline_pressure: "${selectedStudent.timeline_pressure || 'comprehensive'}"`);
            console.log(`[TimelineBuilder] Timeline Builder Result: ${format(scheduledDate, 'yyyy-MM-dd')}`);
            console.log(`[TimelineBuilder] ⚠️  Verify this matches AdminMilestoneService.generateMilestones() output!`);
          } else {
            console.log(`[TimelineBuilder] 🔍 PREVIEW MODE (no student selected) - using fallback anchor dates`);
          }

          // Calculate canvas position
          const daysSinceStart = differenceInDays(scheduledDate, timelineStart);
          const xPosition = daysSinceStart * TIMELINE_CONFIG.dayWidth;
          
          // Get category lane position
          const baseLaneY = getCategoryLaneY(template.category);
          
          console.log(`[TimelineBuilder] Canvas position: x=${xPosition}px, y=${baseLaneY}px (${daysSinceStart} days from timeline start)`);
          console.log(`[TimelineBuilder] ===== END ${template.title} =====\n`);

          return {
            ...template,
            scheduledDate,
            adjustedOffsetDays,
            currentOffsetDays: currentOffset.days, // Current view's offset
            currentOffsetDirection: offsetDirection,
            currentView: timelineView,
            xPosition,
            baseLaneY,
            yPosition: baseLaneY, // Will be adjusted for stacking
            width: Math.max(TIMELINE_CONFIG.minTaskWidth, Math.min(TIMELINE_CONFIG.maxTaskWidth, template.title.length * 8)),
            stackLevel: 0 // Will be calculated
          };
        })
      );

      // Calculate vertical stacking for overlapping tasks in same category/day
      const stackedTemplates = templatesWithPositions.map(template => {
        // Find overlapping tasks in same category
        const overlappingTasks = templatesWithPositions.filter(other => 
          other.category === template.category &&
          other.id !== template.id &&
          Math.abs(other.xPosition - template.xPosition) < TIMELINE_CONFIG.minTaskWidth
        );

        // Sort by priority and original position to maintain consistency
        const allTasksAtPosition = [template, ...overlappingTasks].sort((a, b) => {
          const priorityOrder = { 'critical': 3, 'high': 2, 'medium': 1, 'low': 0 };
          const priorityDiff = (priorityOrder[b.priority] || 1) - (priorityOrder[a.priority] || 1);
          if (priorityDiff !== 0) return priorityDiff;
          return a.id.localeCompare(b.id); // Consistent ordering
        });

        // Find this task's position in the stack
        const stackLevel = allTasksAtPosition.findIndex(t => t.id === template.id);
        
        // Create a unique stack group ID for this position
        const stackGroupId = `${template.category}-${Math.round(template.xPosition / TIMELINE_CONFIG.minTaskWidth)}`;
        const isStackExpanded = expandedStacks.has(stackGroupId);
        
        // Calculate stacked Y position - if expanded, show all tasks separately
        const taskHeight = TIMELINE_CONFIG.taskHeight + 5;
        let adjustedY;
        
        if (isStackExpanded || allTasksAtPosition.length === 1) {
          // Show all stacked tasks separately when expanded
          adjustedY = template.baseLaneY + (stackLevel * (taskHeight + 10)); // 10px gap when expanded
        } else {
          // Compressed stacking when collapsed
          adjustedY = template.baseLaneY + (stackLevel * (taskHeight * 0.7)); // 70% overlap when collapsed
        }

        return {
          ...template,
          stackLevel,
          stackGroupId,
          yPosition: adjustedY,
          isStacked: stackLevel > 0,
          isStackExpanded,
          totalStackedTasks: allTasksAtPosition.length,
          allTasksInStack: allTasksAtPosition
        };
      });

      // Adjust timeline start to include all tasks (especially those scheduled in the past)
      if (stackedTemplates.length > 0) {
        const earliestDate = stackedTemplates.reduce((earliest, task) => {
          return task.scheduledDate < earliest ? task.scheduledDate : earliest;
        }, stackedTemplates[0].scheduledDate);
        
        const latestDate = stackedTemplates.reduce((latest, task) => {
          return task.scheduledDate > latest ? task.scheduledDate : latest;
        }, stackedTemplates[0].scheduledDate);
        
        // Add buffer before earliest and after latest
        const bufferDays = 30;
        const newTimelineStart = subDays(earliestDate, bufferDays);
        const newTimelineEnd = addDays(latestDate, bufferDays);
        
        // Only update if significantly different to avoid infinite loops
        if (Math.abs(differenceInDays(newTimelineStart, timelineStart)) > 5) {
          setTimelineStart(newTimelineStart);
        }
        if (Math.abs(differenceInDays(newTimelineEnd, timelineEnd)) > 5) {
          setTimelineEnd(newTimelineEnd);
        }
      }

      console.log(`[TimelineBuilder] Calculated positions for ${stackedTemplates.length} templates`);
      
      // Apply round organization if enabled
      let organizedTemplates = stackedTemplates;
      if (roundOrganization !== 'none') {
        console.log(`[TimelineBuilder] Applying round organization: ${roundOrganization}`);
        organizedTemplates = applyRoundOrganization(stackedTemplates, roundOrganization);
      }
      
      setPositionedTemplates(organizedTemplates);
    } catch (error) {
      console.error('[TimelineBuilder] Error calculating positioned templates:', error);
      setPositionedTemplates([]);
    }
  }, [filteredTemplates, zoomLevel, timelineView, selectedStudentId, students, expandedStacks, roundOrganization]);

  // Toggle stack expansion
  const toggleStackExpansion = (stackGroupId) => {
    setExpandedStacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stackGroupId)) {
        newSet.delete(stackGroupId);
      } else {
        newSet.add(stackGroupId);
      }
      return newSet;
    });
  };

  // Get color for category
  const getCategoryColor = (category) => {
    const colors = {
      'application': '#ef4444', // red-500
      'essay': '#3b82f6', // azure-500
      'testing': '#10b981', // emerald-500
      'financial_aid': '#f59e0b', // yellow-500
      'research': '#8b5cf6', // violet-500
      'profile': '#6366f1', // indigo-500
      'strategy': '#ec4899' // pink-500
    };
    return colors[category] || '#6b7280'; // stone-500
  };

  // Handle drag and drop operations (calculate offset from drop date to anchor)
  const handleDragEnd = useCallback(async (result) => {
    console.log(`[DragDrop DEBUG] handleDragEnd called with:`, result);
    
    const { destination, source, draggableId } = result;
    
    // If dropped outside a valid drop zone, do nothing
    if (!destination) {
      console.log(`[DragDrop DEBUG] No destination, aborting`);
      return;
    }
    
    // If dropped in the same position, do nothing
    if (destination.droppableId === source.droppableId) {
      console.log(`[DragDrop DEBUG] Same position, aborting`);
      return;
    }
    
    try {
      // Find the template
      const templateId = draggableId;
      const template = templates.find(t => t.id === templateId);
      
      console.log(`[DragDrop DEBUG] Template found:`, template);
      
      if (!template) {
        console.error('Template not found:', templateId);
        return;
      }
      
      // Parse the destination date
      const dropDate = parseISO(destination.droppableId);
      const anchorId = template.anchor_id || 'timeline_baseline';
      
      console.log(`[DragDrop] Dropping task "${template.title}" on ${format(dropDate, 'yyyy-MM-dd')}`);
      console.log(`[DragDrop DEBUG] Anchor ID: ${anchorId}`);
      
      // Get anchor date and calculate offset
      console.log(`[DragDrop DEBUG] Getting anchor date...`);
      const anchorDate = await getAnchorDate(anchorId);
      console.log(`[DragDrop DEBUG] Got anchor date:`, anchorDate);
      
      const daysDifference = differenceInDays(dropDate, anchorDate);
      console.log(`[DragDrop DEBUG] Days difference: ${daysDifference}`);
      
      let offsetDirection, offsetDays;
      if (daysDifference >= 0) {
        offsetDirection = 'after';
        offsetDays = daysDifference;
      } else {
        offsetDirection = 'before'; 
        offsetDays = Math.abs(daysDifference);
      }
      
      console.log(`[DragDrop] Anchor: ${anchorId} (${format(anchorDate, 'yyyy-MM-dd')})`);
      console.log(`[DragDrop] Calculated: ${offsetDays}d ${offsetDirection}`);
      
      // Update the template with new signed offset
      const signedOffset = offsetDirection === 'before' ? -offsetDays : offsetDays;
      const updates = {
        offset_days: signedOffset,
        last_updated: new Date().toISOString()
      };
      
      console.log(`[DragDrop DEBUG] About to update template with:`, updates);
      
      // Update local templates state immediately
      setTemplates(prev => prev.map(t => 
        t.id === templateId ? { ...t, ...updates } : t
      ));
      
      // Update the database
      const { error } = await supabase
        .from('milestone_templates')
        .update(updates)
        .eq('id', templateId);
      
      if (error) {
        console.error('Error updating template offset:', error);
        // Revert local state on error
        setTemplates(prev => prev.map(t => 
          t.id === templateId ? template : t
        ));
        return;
      }
      
      console.log(`[DragDrop] Successfully updated offset to ${offsetDays}d ${offsetDirection}`);
      
    } catch (error) {
      console.error('Error in handleDragEnd:', error);
    }
  }, [templates, setTemplates]);

  // Cache anchor dates to prevent looping
  const [anchorDatesCache, setAnchorDatesCache] = useState({});
  
  // Calculate all anchor dates once and cache them
  const calculateAllAnchorDates = useCallback(async () => {
    if (!selectedStudentId || students.length === 0) return {};
    
    const student = students.find(s => s.id === selectedStudentId);
    if (!student) return {};
    
    const anchorTypes = [
      'timeline_baseline',
      'senior_year_start', 
      'fafsa_opens',
      'ed_deadline',
      'ea_deadline', 
      'rd_deadline',
      'finaid_deadline',
      'css_deadline'
    ];
    
    const cache = {};
    
    for (const anchorId of anchorTypes) {
      try {
        const anchorDate = await getAnchorDate(anchorId);
        cache[anchorId] = {
          id: anchorId,
          name: anchorId.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          date: anchorDate,
          dateStr: format(anchorDate, 'yyyy-MM-dd')
        };
      } catch (error) {
        // Skip anchors that can't be calculated
        continue;
      }
    }
    
    return cache;
  }, [selectedStudentId, students]);
  
  // Load anchor cache when student changes
  useEffect(() => {
    calculateAllAnchorDates().then(setAnchorDatesCache);
  }, [calculateAllAnchorDates]);
  
  // Get anchors for a specific day (uses cache)
  const getAnchorsForDay = useCallback((date) => {
    const dayStr = format(date, 'yyyy-MM-dd');
    return Object.values(anchorDatesCache).filter(anchor => anchor.dateStr === dayStr);
  }, [anchorDatesCache]);

  

  // Drag-and-Drop Calendar Day Component with Anchor Display
  const CalendarDay = ({ date, tasks }) => {
    const isCurrentMonth = calendarViewMode === 'week' ? true : isSameMonth(date, currentCalendarDate);
    const isToday = isSameDay(date, new Date());
    const dayId = format(date, 'yyyy-MM-dd');
    
    // Get anchors for this day (synchronous now)
    const anchors = getAnchorsForDay(date);
    
    return (
      <Droppable droppableId={dayId} key={dayId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-[100px] p-2 border border-stone-200 transition-colors ${
              isCurrentMonth ? 'bg-white' : 'bg-stone-50'
            } ${isToday ? 'ring-2 ring-azure-500' : ''} ${
              snapshot.isDraggedOver ? 'bg-azure-50 border-azure-300' : ''
            } ${anchors.length > 0 ? 'border-l-4 border-l-violet-500' : ''}`}
            onClick={() => {
              // Optional: allow clicking on day to select it
            }}
          >
            <div className={`text-sm font-medium mb-2 ${
              isCurrentMonth ? 'text-slate-900' : 'text-stone-400'
            }`}>
              {format(date, 'd')}
            </div>
            
            {/* Anchor indicators */}
            {anchors.length > 0 && (
              <div className="mb-2 space-y-1">
                {anchors.map(anchor => (
                  <div 
                    key={anchor.id}
                    className="text-xs px-2 py-1 bg-violet-100 text-violet-700 rounded border-l-2 border-violet-500"
                    title={`Anchor: ${anchor.name}`}
                  >
                    <div className="font-medium truncate">📍 {anchor.name}</div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="space-y-1">
              {tasks.slice(0, calendarViewMode === 'week' ? 8 : 3).map((task, index) => (
                <Draggable key={task.id} draggableId={task.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!snapshot.isDragging) {
                          setSelectedTask(selectedTask?.id === task.id ? null : task);
                        }
                      }}
                      className={`text-xs p-1 rounded cursor-pointer border-l-2 transition-all hover:shadow-sm ${
                        selectedTask?.id === task.id 
                          ? 'ring-2 ring-azure-200 shadow-sm' 
                          : 'hover:shadow-sm'
                      } ${snapshot.isDragging ? 'opacity-75 shadow-lg z-50' : ''}`}
                      style={{
                        backgroundColor: `${getCategoryColor(task.category)}20`,
                        borderLeftColor: getCategoryColor(task.category),
                        color: getCategoryColor(task.category),
                        ...provided.draggableProps.style
                      }}
                      title={task.title}
                    >
                      <div className="truncate font-medium">
                        {task.title}
                      </div>
                      <div className="text-xs opacity-75">
                        {task.adjustedOffsetDays > 0 ? '+' : ''}{task.adjustedOffsetDays}d
                      </div>
                      {/* Application Round Indicators */}
                      {hasApplicationRoundModifiers(task) && (
                        <div className="flex gap-1 mt-1">
                          {getTaskRoundIndicators(task).map(({ round, config }) => {
                            const IconComponent = config.icon;
                            return (
                              <div 
                                key={round}
                                className="flex items-center justify-center w-3 h-3 rounded-full text-xs"
                                style={{ 
                                  backgroundColor: config.color,
                                  color: 'white'
                                }}
                                title={`${config.name} modifier`}
                              >
                                <IconComponent size={8} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {tasks.length > (calendarViewMode === 'week' ? 8 : 3) && (
                <div className="text-xs text-stone-500 pl-1">
                  +{tasks.length - (calendarViewMode === 'week' ? 8 : 3)} more
                </div>
              )}
              {provided.placeholder}
            </div>
          </div>
        )}
      </Droppable>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-2 text-stone-500">
          <Clock className="w-5 h-5 animate-spin" />
          <span>Loading timeline builder...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-stone-50 min-h-screen ${isFullscreen ? 'fixed inset-0 z-50' : 'p-6'}`}>
      {/* Header */}
      <div className="mb-6 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Interactive Timeline Builder</h1>
            <p className="text-slate-600">Visual drag-and-drop timeline management with dependency validation</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium bg-stone-100 text-slate-700 border border-stone-200 hover:bg-stone-200"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
            </button>
          </div>
        </div>

        {/* Timeline View Selector */}
        <div className="bg-white rounded-lg p-4 mb-6 bg-slate-900">
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-stone-50" />
              <span className="text-sm font-medium text-stone-50">WorkingView:</span><span className="text-sm text-indigo-100">
               {VIEW_CONFIGS[timelineView].description}
            </span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-stone-50 uppercase tracking-wider">Regular Decision</h4>
              <div className="space-y-1 flex flex-row gap-2">
                {Object.entries(VIEW_CONFIGS).filter(([key]) => key.includes('rd')).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setTimelineView(key)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        timelineView === key
                          ? `${config.color} text-white shadow-sm`
                          : 'bg-stone-50 text-slate-700 border border-stone-200 hover:bg-stone-100'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className="w-4 h-4" />
                        <span>{config.label}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        timelineView === key 
                          ? 'bg-white/20 text-white' 
                          : 'bg-stone-200 text-slate-600'
                      }`}>
                        {config.scope === 'comprehensive' ? '18mo' : 'Crisis'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-stone-50 uppercase tracking-wider">Early Decision</h4>
              <div className="space-y-1 flex flex-row gap-2">
                {Object.entries(VIEW_CONFIGS).filter(([key]) => key.includes('ed')).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setTimelineView(key)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        timelineView === key
                          ? `${config.color} text-white shadow-sm`
                          : 'bg-stone-50 text-slate-700 border border-stone-200 hover:bg-stone-100'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className="w-4 h-4" />
                        <span>{config.label}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        timelineView === key 
                          ? 'bg-white/20 text-white' 
                          : 'bg-stone-200 text-slate-600'
                      }`}>
                        {config.scope === 'comprehensive' ? '18mo' : 'Crisis'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
         
        </div>

        {/* Controls Row 1: Student Context and Focus */}
        <div className="flex mb-2">
          <div className="flex space-x-6 gap-2 flex-wrap">
            {/* Student Context Selector */}
            <div className="flex space-x-2">
              <Users className="w-4 h-4 text-stone-500" />
              <span className="text-sm font-medium text-slate-700">Context:</span>
              <select
                value={selectedStudentId || ''}
                onChange={(e) => setSelectedStudentId(e.target.value || null)}
                className="text-sm border border-stone-200 rounded px-3 py-1.5 min-w-48"
              >
                <option value="">Preview Mode (Mock Dates)</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.first_name} {student.last_name} ({student.graduation_year})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Anchor/Deadline Focus */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-stone-500" />
              <span className="text-sm font-medium text-slate-700">Focus:</span>
              <select
                value={focusAnchor}
                onChange={(e) => setFocusAnchor(e.target.value)}
                className="text-sm border border-stone-200 rounded px-3 py-1.5"
              >
                <option value="all">All Deadlines</option>
                <option value="ed_ea">Early Decision/Action</option>
                <option value="rd">Regular Decision</option>
                <option value="rolling_admission">Rolling Admission</option>
              </select>
            </div>
            
            {/* Application Round Modifier */}
            <div className="flex items-center space-x-2">
              <Layers className="w-4 h-4 text-stone-500" />
              <span className="text-sm font-medium text-slate-700">Modifiers:</span>
              <select
                value={selectedApplicationRound}
                onChange={(e) => setSelectedApplicationRound(e.target.value)}
                className="text-sm border border-stone-200 rounded px-3 py-1.5"
              >
                {Object.entries(APPLICATION_ROUNDS).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.name} ({config.shortName})
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowRoundModifiers(!showRoundModifiers)}
                className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                  showRoundModifiers 
                    ? 'bg-azure-500 text-white border-azure-500' 
                    : 'bg-white text-slate-700 border-stone-300 hover:bg-stone-50'
                }`}
                title="Toggle round modifier editing panel"
              >
                <Settings className="w-3 h-3" />
              </button>
            </div>
          </div>
          
        </div>
        </div>

        {/* Controls Row 2: Category Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium text-slate-700">Categories:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => {
                  if (category === 'all') {
                    setSelectedCategories(['all']);
                  } else {
                    setSelectedCategories(prev => 
                      prev.includes('all') 
                        ? [category]
                        : prev.includes(category)
                          ? prev.filter(c => c !== category)
                          : [...prev.filter(c => c !== 'all'), category]
                    );
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectedCategories.includes(category) || (category !== 'all' && selectedCategories.includes('all'))
                    ? 'text-white'
                    : 'bg-white text-slate-600 border border-stone-200 hover:bg-stone-50'
                }`}
                style={{
                  backgroundColor: selectedCategories.includes(category) || (category !== 'all' && selectedCategories.includes('all'))
                    ? getCategoryColor(category)
                    : undefined
                }}
              >
                {category === 'all' ? 'All Categories' : category.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>
        </div>
     

      {/* Conflicts and Suggestions */}
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conflicts Summary */}
        {conflicts.length > 0 && (
          <div className="bg-white rounded-lg border-2 border-red-200">
            <div className="p-4 bg-red-50 border-b border-red-200">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-900">Dependency Conflicts</h3>
                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                  {conflicts.length}
                </span>
              </div>
            </div>
            <div className="p-4 max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {conflicts.map(conflict => (
                  <div key={conflict.id} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                    {conflict.message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

</div>

      {/* Round Organization Controls */}
      <div className="bg-white rounded-lg border border-stone-200 p-4 mb-2 flex flex-row justify-between">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-slate-700">Organization:</span>
              <select
                value={roundOrganization}
                onChange={(e) => setRoundOrganization(e.target.value)}
                className="text-sm border border-stone-200 rounded px-3 py-1.5"
              >
                <option value="none">Standard View</option>
                <option value="group">Group by Round</option>
                <option value="priority">Priority by Round</option>
              </select>
            </div>
            
            {roundOrganization !== 'none' && (
              <div className="flex items-center space-x-2 text-xs text-slate-600">
                <span>Round Legend:</span>
                {Object.entries(APPLICATION_ROUNDS).map(([key, config]) => {
                  const IconComponent = config.icon;
                  return (
                    <div key={key} className="flex items-center space-x-1">
                      <div 
                        className="flex items-center justify-center w-4 h-4 rounded-full"
                        style={{ backgroundColor: config.color }}
                      >
                        <IconComponent size={8} className="text-white" />
                      </div>
                      <span className="text-xs">{config.shortName}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {roundOrganization !== 'none' && (
            <div className="text-xs text-stone-500">
              Tasks with round modifiers are organized by their {roundOrganization === 'group' ? 'application rounds' : 'priority levels'}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
            {/* Zoom Controls */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">Zoom:</span>
              <button
                onClick={() => setZoomLevel(0.5)}
                className="px-2 py-1 text-xs border rounded hover:bg-stone-50"
                title="Minimum zoom (50%)"
              >
                Min
              </button>
              <button
                onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}
                className="px-2 py-1 text-sm border rounded hover:bg-stone-50"
              >
                -
              </button>
              <span className="text-sm font-medium min-w-12 text-center">{Math.round(zoomLevel * 100)}%</span>
              <button
                onClick={() => setZoomLevel(prev => Math.min(10, prev + 0.25))}
                className="px-2 py-1 text-sm border rounded hover:bg-stone-50"
              >
                +
              </button>
              <button
                onClick={() => setZoomLevel(10)}
                className="px-2 py-1 text-xs border rounded hover:bg-stone-50"
                title="Maximum zoom (1000%)"
              >
                Max
              </button>
            </div>
            
            {/* Auto-Optimize Button */}
            <button
              onClick={optimizeTimelineSpacing}
              className="flex items-center space-x-2 px-3 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 text-sm"
            >
              <Zap className="w-4 h-4" />
              <span>Auto-Optimize</span>
            </button>
          </div>
      </div>

      {/* Calendar View */}
      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">
              {displayMode === 'calendar' ? 'Calendar' : 'List'} Timeline - {VIEW_CONFIGS[timelineView].label}
            </h3>
            <div className="flex items-center gap-4">
              <div className="text-xs text-slate-600">
                {filteredTemplates.length} tasks • {conflicts.length} conflicts
              </div>
              
              {/* Display Mode Toggle */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
                  <button
                    onClick={() => setDisplayMode('calendar')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                      displayMode === 'calendar'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Calendar
                  </button>
                  <button
                    onClick={() => setDisplayMode('list')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                      displayMode === 'list'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    List
                  </button>
                </div>
                
                {displayMode === 'calendar' && (
                  <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
                    <button
                      onClick={() => setCalendarViewMode('month')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        calendarViewMode === 'month'
                          ? 'bg-white text-indigo-600 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Month
                    </button>
                    <button
                      onClick={() => setCalendarViewMode('week')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        calendarViewMode === 'week'
                          ? 'bg-white text-indigo-600 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Week
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentCalendarDate(
                  calendarViewMode === 'week' 
                    ? subDays(currentCalendarDate, 7) 
                    : subMonths(currentCalendarDate, 1)
                )}
                className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
                title={calendarViewMode === 'week' ? 'Previous week' : 'Previous month'}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <h2 className="text-base font-semibold text-slate-900">
                {calendarViewMode === 'week' 
                  ? `Week of ${format(startOfWeek(currentCalendarDate), 'MMM d')} - ${format(endOfWeek(currentCalendarDate), 'MMM d, yyyy')}`
                  : format(currentCalendarDate, 'MMMM yyyy')
                }
              </h2>
              
              <button
                onClick={() => setCurrentCalendarDate(
                  calendarViewMode === 'week' 
                    ? addDays(currentCalendarDate, 7) 
                    : addMonths(currentCalendarDate, 1)
                )}
                className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
                title={calendarViewMode === 'week' ? 'Next week' : 'Next month'}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            <button
              onClick={() => setCurrentCalendarDate(new Date())}
              className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Today
            </button>
          </div>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 bg-slate-50">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar View */}
        {displayMode === 'calendar' && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-7" style={{ minHeight: '500px' }}>
              {positionedTemplates.length === 0 && templates.length > 0 ? (
                <div className="col-span-7 flex items-center justify-center text-stone-500 py-12">
                  <div className="text-center">
                    <div className="text-sm">Calculating timeline positions...</div>
                    <div className="text-xs mt-1">Please wait</div>
                  </div>
                </div>
              ) : (
                calendarDays.map(day => (
                  <CalendarDay
                    key={format(day, 'yyyy-MM-dd')}
                    date={day}
                    tasks={getTasksForDay(day)}
                  />
                ))
              )}
            </div>
          </DragDropContext>
        )}

        {/* List View - Enhanced Spreadsheet Interface */}
        {displayMode === 'list' && (
          <TimelineSpreadsheet
            positionedTemplates={positionedTemplates}
            timelineView={timelineView}
            selectedCategories={selectedCategories}
            anchorDatesCache={anchorDatesCache}
            onUpdateTemplate={updateTemplate}
            onBulkUpdate={(templateIds, updates) => {
              // Bulk update functionality - iterate through selected templates
              templateIds.forEach(async (templateId) => {
                await updateTemplate(templateId, updates);
              });
            }}
            TASK_CATEGORIES={TASK_CATEGORIES}
            APPLICATION_ROUNDS={APPLICATION_ROUNDS}
            getCategoryColor={getCategoryColor}
          />
        )}
      </div>

      {/* Selected Task Details Panel */}
      {selectedTask && (
        <div className="mb-6 bg-white rounded-lg border border-stone-200">
          <div className="p-4 border-b border-stone-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Task Details</h3>
              <button 
                onClick={() => setSelectedTask(null)}
                className="text-stone-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-2 flex flex-col gap-2">
              {/* Task Header */}
              <div className="flex flex-row gap-2 justify-start align-middle">
                  <div className="px-2 py-2 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${getCategoryColor(selectedTask.category)}20`,
                      color: getCategoryColor(selectedTask.category)
                    }}>
                    {selectedTask.category.replace('_', ' ').toUpperCase()}
                  </div>
                <h4 className="text-lg font-semibold text-slate-900">
                  {selectedTask.title}
                </h4>
     
                  <div className="text-coral-500 text-sm mt-1">
                    Due: {selectedTask.scheduledDate ? format(new Date(selectedTask.scheduledDate), 'MMM d, yyyy') : 'Calculating...'}
                  </div>
              </div>

                <p className="text-sm text-slate-600 pb-3">
                  {selectedTask.description || 'No description provided'}
                </p>
   

              {/* Date Picker for Visual Offset Setting */}
              <div className="flex flex-col gap-2">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Set Task Date (calculates offset automatically)
                  </label> 
              <div className="flex flex-row gap-2">
             
              
                <div>
                  
                  <div className="flex flex-row gap-2 w-fit align-baseline ">
                    <input
                      type="text"
                      placeholder="YYYY-MM-DD"
                      className="flex-1 px-3 py-1 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500 text-sm"
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          const value = e.target.value;
                          if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                            // Call the same date processing logic
                            if (!value) return;
                      
                            setIsCalculating(true);
                            console.log(`[DatePicker DEBUG] User typed date: ${value}`);
                            
                            try {
                              // Calculate offset from chosen date to anchor (local timezone handling)
                              const [year, month, day] = value.split('-').map(Number);
                              const chosenDate = new Date(year, month - 1, day); // Local timezone
                              const anchorId = selectedTask.anchor_id || 'timeline_baseline';
                              
                              const anchorDate = await getAnchorDate(anchorId);
                              
                              // Calculate what the RAW offset would need to be to hit the target date
                              const rawDaysDifference = differenceInDays(chosenDate, anchorDate);
                              
                              let offsetDirection, rawOffsetDays;
                              if (rawDaysDifference >= 0) {
                                offsetDirection = 'after';
                                rawOffsetDays = rawDaysDifference;
                              } else {
                                offsetDirection = 'before'; 
                                rawOffsetDays = Math.abs(rawDaysDifference);
                              }
                              
                              const templateOffsetDays = rawOffsetDays;
                              
                              // Update the correct field based on current view
                              const fields = getOffsetFields(timelineView);
                              const updateData = {
                                [fields.days]: templateOffsetDays,
                                [fields.direction]: offsetDirection
                              };
                              
                              // Also update the corresponding target date field
                              const targetDateField = (() => {
                                switch(timelineView) {
                                  case 'max_rd': return 'max_rd_target_date';
                                  case 'min_rd': return 'min_rd_target_date';
                                  case 'max_ed': return 'max_ed_target_date';
                                  case 'min_ed': return 'min_ed_target_date';
                                  default: return 'max_rd_target_date';
                                }
                              })();
                              
                              const targetDateValue = format(chosenDate, 'yyyy-MM-dd');
                              updateData[targetDateField] = targetDateValue;
                              
                              await updateTemplate(selectedTask.id, updateData);
                              
                              const signedOffset = offsetDirection === 'before' ? -templateOffsetDays : templateOffsetDays;
                              setSelectedTask(prev => ({
                                ...prev,
                                [fieldName]: signedOffset,
                                [targetDateField]: targetDateValue
                              }));
                              
                            } catch (error) {
                              console.error('Error calculating offset from typed date:', error);
                            } finally {
                              setIsCalculating(false);
                            }
                          }
                        }
                      }}
                    />
                    <input
                      type="date"
                    value={(() => {
                      // Calculate current display date from view-specific offset
                      if (!selectedTask.anchor_id) return '';
                      
                      try {
                        const anchorId = selectedTask.anchor_id;
                        const anchorDateObj = anchorDatesCache[anchorId];
                        if (!anchorDateObj?.date) return '';
                        
                        // Ensure anchor date is in local timezone
                        let anchorDate;
                        try {
                          if (anchorDateObj.date instanceof Date) {
                            anchorDate = new Date(anchorDateObj.date.getTime());
                          } else {
                            anchorDate = new Date(anchorDateObj.date + 'T00:00:00');
                          }
                          
                          // Check if date is valid
                          if (isNaN(anchorDate.getTime())) {
                            console.warn('Could not parse anchor date:', anchorDateObj.date);
                            return '';
                          }
                        } catch (error) {
                          console.warn('Error parsing anchor date:', anchorDateObj.date, error);
                          return '';
                        }
                        
                        const currentOffset = getOffsetValue(selectedTask, timelineView);
                        if (!currentOffset.days || isNaN(currentOffset.days)) {
                          console.warn('Invalid offset days:', currentOffset);
                          return '';
                        }
                        
                        const displayDate = currentOffset.direction === 'after' 
                          ? addDays(anchorDate, currentOffset.days)
                          : subDays(anchorDate, currentOffset.days);
                        
                        if (isNaN(displayDate.getTime())) {
                          console.warn('Invalid display date calculated:', displayDate);
                          return '';
                        }
                        
                        return format(displayDate, 'yyyy-MM-dd');
                      } catch (error) {
                        console.warn('Error calculating display date:', error);
                        return '';
                      }
                    })()}
                    onChange={async (e) => {
                      if (!e.target.value) return;
                      
                      setIsCalculating(true);
                      console.log(`[DatePicker DEBUG] User picked date: ${e.target.value}`);
                      
                      // Calculate offset from chosen date to anchor (local timezone handling)
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      const chosenDate = new Date(year, month - 1, day); // Local timezone
                      const anchorId = selectedTask.anchor_id || 'timeline_baseline';
                      
                      console.log(`[DatePicker DEBUG] Chosen date object:`, chosenDate);
                      console.log(`[DatePicker DEBUG] Anchor ID: ${anchorId}`);
                      console.log(`[DatePicker DEBUG] Selected task:`, selectedTask);
                      
                      try {
                        // Get anchor date
                        console.log(`[DatePicker DEBUG] Getting anchor date for: ${anchorId}`);
                        const anchorDate = await getAnchorDate(anchorId);
                        console.log(`[DatePicker DEBUG] Got anchor date:`, anchorDate);
                        
                        // Calculate what the RAW offset would need to be to hit the target date
                        const rawDaysDifference = differenceInDays(chosenDate, anchorDate);
                        console.log(`[DatePicker DEBUG] Raw days difference: ${rawDaysDifference}`);
                        
                        let offsetDirection, rawOffsetDays;
                        if (rawDaysDifference >= 0) {
                          offsetDirection = 'after';
                          rawOffsetDays = rawDaysDifference;
                        } else {
                          offsetDirection = 'before'; 
                          rawOffsetDays = Math.abs(rawDaysDifference);
                        }
                        
                        // In the new min/max system, we store RAW offset days directly
                        // No pressure calculations needed - each view stores its own raw values
                        const templateOffsetDays = rawOffsetDays;
                        
                        console.log(`[DatePicker] Target date: ${format(chosenDate, 'yyyy-MM-dd')}`);
                        console.log(`[DatePicker] Anchor: ${format(anchorDate, 'yyyy-MM-dd')}`);
                        console.log(`[DatePicker] Calculated offset: ${templateOffsetDays}d ${offsetDirection}`);
                        console.log(`[DatePicker] Storing to view: ${timelineView}`);
                        console.log(`[DatePicker] Update data:`, updateData);
                        
                        // Convert to signed offset
                        const signedOffset = offsetDirection === 'before' ? -templateOffsetDays : templateOffsetDays;
                        console.log(`[DatePicker DEBUG] About to update template with signed offset: ${signedOffset}`);

                        // Update the correct field based on current view
                        const fieldName = getOffsetFields(timelineView);
                        const updateData = {
                          [fieldName]: signedOffset
                        };
                        
                        // Also update the corresponding target date field
                        const targetDateField = (() => {
                          switch(timelineView) {
                            case 'max_rd': return 'max_rd_target_date';
                            case 'min_rd': return 'min_rd_target_date';
                            case 'max_ed': return 'max_ed_target_date';
                            case 'min_ed': return 'min_ed_target_date';
                            default: return 'max_rd_target_date';
                          }
                        })();
                        
                        const targetDateValue = format(chosenDate, 'yyyy-MM-dd');
                        updateData[targetDateField] = targetDateValue;
                        
                        await updateTemplate(selectedTask.id, updateData);
                        
                        setSelectedTask(prev => ({
                          ...prev,
                          [fieldName]: signedOffset,
                          [targetDateField]: targetDateValue
                        }));
                        
                        console.log(`[DatePicker DEBUG] Update completed - should now show ${format(chosenDate, 'yyyy-MM-dd')}`);
                        
                      } catch (error) {
                        console.error('Error calculating offset from date:', error);
                      } finally {
                        setIsCalculating(false);
                      }
                    }}
                    className="px-3 py-1 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>  
                <div>
                <div className="">
                  <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">
                    Anchor Point
                  </label>
                  <select
                    value={selectedTask.anchor_id || 'timeline_baseline'}
                    onChange={async (e) => {
                      const newAnchorId = e.target.value;
                      
                      // Get current target date from date picker display
                      const currentDisplayDate = (() => {
                        try {
                          const anchorDateObj = anchorDatesCache[selectedTask.anchor_id];
                          if (!anchorDateObj?.date) return null;
                          
                          const anchorDate = new Date(anchorDateObj.date + 'T00:00:00');
                          const currentOffset = getOffsetValue(selectedTask, timelineView);
                          
                          return currentOffset.direction === 'after' 
                            ? addDays(anchorDate, currentOffset.days)
                            : subDays(anchorDate, currentOffset.days);
                        } catch (error) {
                          return null;
                        }
                      })();
                      
                      // Update anchor first
                      await updateTemplate(selectedTask.id, {
                        anchor_id: newAnchorId
                      });
                      
                      // If we have a current display date, recalculate offset for new anchor
                      if (currentDisplayDate) {
                        try {
                          const newAnchorDate = await getAnchorDate(newAnchorId);
                          const rawDaysDifference = differenceInDays(currentDisplayDate, newAnchorDate);
                          
                          let offsetDirection, rawOffsetDays;
                          if (rawDaysDifference >= 0) {
                            offsetDirection = 'after';
                            rawOffsetDays = rawDaysDifference;
                          } else {
                            offsetDirection = 'before'; 
                            rawOffsetDays = Math.abs(rawDaysDifference);
                          }
                          
                          // Update offset for new anchor while preserving target date
                          const fieldName = getOffsetFields(timelineView);
                          const signedOffset = offsetDirection === 'before' ? -rawOffsetDays : rawOffsetDays;
                          await updateTemplate(selectedTask.id, {
                            [fieldName]: signedOffset
                          });

                          setSelectedTask(prev => ({
                            ...prev,
                            anchor_id: newAnchorId,
                            [fieldName]: signedOffset
                          }));
                        } catch (error) {
                          console.error('Error recalculating offset for new anchor:', error);
                          setSelectedTask(prev => ({
                            ...prev,
                            anchor_id: newAnchorId
                          }));
                        }
                      } else {
                        setSelectedTask(prev => ({
                          ...prev,
                          anchor_id: newAnchorId
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  >
                    <option value="timeline_baseline">Student Planning Start Date</option>
                    <option value="senior_year_start">Senior Year Start (Sep 1)</option>
                    <option value="fafsa_opens">FAFSA Opens (Oct 1)</option>
                    <option value="ed_deadline">Early Decision Deadline (Nov 1)</option>
                    <option value="ea_deadline">Early Action Deadline (Nov 1)</option>
                    <option value="rd_deadline">Regular Decision Deadline (Jan 1)</option>
                    <option value="finaid_deadline">Financial Aid Deadline (Feb 1)</option>
                    <option value="css_profile_opens">CSS Profile Opens (Oct 1)</option>
                    <option value="national_decision_day">National Decision Day (May 1)</option>
                    <option value="graduation">Graduation (Jun 1)</option>
                    <option value="task_completion">Task Completion</option>
                  </select>
                </div>
                </div>
                </div>
              </div>
</div>
              {/* Anchor and Offset Controls */}
              <div className="pt-2 flex flex-row gap-4 flex-wrap">
               

                 
                  <div className=" bg-stone-50 rounded-lg p-2  w-20 overflow-hidden">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Offset
                    </label>
                    <input
                      type="number"
                      value={isCalculating ? '...' : (getOffsetValue(selectedTask, timelineView) || 0)}
                      onChange={async (e) => {
                        const offsetDays = parseInt(e.target.value) || 0;
                        const fieldName = getOffsetFields(timelineView);
                        await updateTemplate(selectedTask.id, {
                          [fieldName]: offsetDays
                        });
                        setSelectedTask(prev => ({
                          ...prev,
                          [fieldName]: offsetDays
                        }));
                      }}
                      className=" bg-stone-50 rounded-lg "
                      placeholder="Days"
                    />
                  </div>
              

              {/* Task Configuration */}
             
                <div>
                  <label className="block w-full text-sm font-medium text-slate-700 mb-2">
                    Priority Level
                  </label>
                  <select
                    value={selectedTask.priority_level || 'medium'}
                    onChange={async (e) => {
                      await updateTemplate(selectedTask.id, {
                        priority_level: e.target.value
                      });
                      setSelectedTask(prev => ({
                        ...prev,
                        priority_level: e.target.value
                      }));
                    }}
                    className="w-fit px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Est. Time (hours)
                  </label>
                  <input
                    type="number"
                    value={selectedTask.estimated_time_hours || 1}
                    onChange={async (e) => {
                      const hours = parseFloat(e.target.value) || 1;
                      await updateTemplate(selectedTask.id, {
                        estimated_time_hours: hours
                      });
                      setSelectedTask(prev => ({
                        ...prev,
                        estimated_time_hours: hours
                      }));
                    }}
                    className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    step="0.5"
                    min="0.5"
                  />
                </div>

              </div>

              {/* Temporary Date Fields for Bulk Entry */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Target Dates (Temporary - for bulk SQL entry)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Max RD Date</label>
                    <input
                      type="date"
                      value={selectedTask.max_rd_target_date || ''}
                      onChange={async (e) => {
                        await updateTemplate(selectedTask.id, {
                          max_rd_target_date: e.target.value || null
                        });
                        setSelectedTask(prev => ({
                          ...prev,
                          max_rd_target_date: e.target.value || null
                        }));
                      }}
                      className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Min RD Date</label>
                    <input
                      type="date"
                      value={selectedTask.min_rd_target_date || ''}
                      onChange={async (e) => {
                        await updateTemplate(selectedTask.id, {
                          min_rd_target_date: e.target.value || null
                        });
                        setSelectedTask(prev => ({
                          ...prev,
                          min_rd_target_date: e.target.value || null
                        }));
                      }}
                      className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Max ED Date</label>
                    <input
                      type="date"
                      value={selectedTask.max_ed_target_date || ''}
                      onChange={async (e) => {
                        await updateTemplate(selectedTask.id, {
                          max_ed_target_date: e.target.value || null
                        });
                        setSelectedTask(prev => ({
                          ...prev,
                          max_ed_target_date: e.target.value || null
                        }));
                      }}
                      className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Min ED Date</label>
                    <input
                      type="date"
                      value={selectedTask.min_ed_target_date || ''}
                      onChange={async (e) => {
                        await updateTemplate(selectedTask.id, {
                          min_ed_target_date: e.target.value || null
                        });
                        setSelectedTask(prev => ({
                          ...prev,
                          min_ed_target_date: e.target.value || null
                        }));
                      }}
                      className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    />
                  </div>
                </div>
                <div className="mt-2 text-xs text-stone-500">
                  💡 These fields allow bulk SQL updates. Use SQL scripts to populate many tasks at once instead of manual entry.
                </div>
              </div>

              {/* Application Round Modifiers */}
              {hasApplicationRoundModifiers(selectedTask) && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Application Round Modifiers
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {getTaskRoundIndicators(selectedTask).map(({ round, config }) => {
                      const IconComponent = config.icon;
                      return (
                        <div 
                          key={round}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                          style={{ 
                            backgroundColor: `${config.color}10`,
                            borderColor: config.color,
                            color: config.color
                          }}
                        >
                          <IconComponent size={16} />
                          <span className="text-sm font-medium">{config.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Anchor Information */}

              <div className="flex flex-row gap-2">

              {selectedTask.anchor_id && (
                <div>
                  
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <div className="text-sm font-medium text-slate-900">
                      {selectedTask.anchor_id.replace('_', ' ').toUpperCase()}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {isCalculating ? (
                        <span className="text-azure-600 italic">Calculating...</span>
                      ) : (
                        `Task is scheduled ${getOffsetValue(selectedTask, timelineView).days || 0} days ${getOffsetValue(selectedTask, timelineView).direction || 'before'} this anchor point`
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
                <button
                  onClick={async () => {
                    // Reduce offset by 7 days (move earlier)
                    const currentOffset = getOffsetValue(selectedTask, timelineView).days || 0;
                    const newOffset = Math.max(0, currentOffset + 7);
                    const fields = getOffsetFields(timelineView);
                    await updateTemplate(selectedTask.id, {
                      [fields.days]: newOffset
                    });
                    setSelectedTask(prev => ({
                      ...prev,
                      [fields.days]: newOffset
                    }));
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-azure-50 text-azure-600 rounded-lg hover:bg-azure-100 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  +7 Days Earlier
                </button>
                <button
                  onClick={async () => {
                    // Increase offset by 7 days (move later if 'before', earlier if 'after')
                    const currentOffset = getOffsetValue(selectedTask, timelineView).days || 0;
                    const newOffset = Math.max(0, currentOffset - 7);
                    const fields = getOffsetFields(timelineView);
                    await updateTemplate(selectedTask.id, {
                      [fields.days]: newOffset
                    });
                    setSelectedTask(prev => ({
                      ...prev,
                      [fields.days]: newOffset
                    }));
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                  <ArrowRight className="w-4 h-4" />
                  -7 Days Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default TimelineBuilder;
